import { Card, Flex, Image, Typography } from '@iheartjane/reefer';
import type { ColorKey, MarginProperties } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import type { MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';
import { clsx } from 'clsx';
import snakeCase from 'lodash-es/snakeCase';

import { alwaysStartsWith } from '@jane/business-admin/util';

import colorWheel from './color-wheel.png';

import styles from './colorPicker.module.css';

export interface ColorPickerProps extends LabeledOption, MarginProperties {
  hasError?: boolean;
  labelAlign?: 'center' | 'left';
  labelColor?: ColorKey;
  onBlur?: () => void;
  onChange: (hex: string) => void;
  shouldStretch?: boolean;
}

const hexColorMask: MaskitoOptions = {
  mask: /^#[0-9a-fA-F]{0,6}$/,
  preprocessors: [alwaysStartsWith('#')],
};

export const ColorPicker = ({
  label,
  labelAlign = 'center',
  labelColor = 'grays-mid',
  onBlur,
  onChange,
  value,
  hasError,
  shouldStretch = false,
  ...marginProps
}: ColorPickerProps) => {
  const inputRef = useMaskito({ options: hexColorMask });
  const inputId = snakeCase(label);
  return (
    <Flex
      alignItems="center"
      flexDirection="column"
      mr={24}
      {...marginProps}
      width={shouldStretch ? '100%' : 'auto'}
    >
      <label
        htmlFor={inputId}
        style={{
          width: shouldStretch ? '100%' : 'auto',
          '--colorPicker-previewColor': value,
        }}
      >
        <Typography
          textAlign={labelAlign}
          variant="small-body-semibold"
          color={labelColor}
          mb={8}
        >
          {label}
        </Typography>
        <Card.Group width={shouldStretch ? '100%' : '177px'} height="136px">
          <Card.Content className={styles['preview']}>&nbsp;</Card.Content>
          <Flex className={styles['content']}>
            <Flex
              alignItems="center"
              background="grays-white"
              justifyContent="space-between"
              width="100%"
            >
              <Flex
                className={styles['colorIcon']}
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  altText="color-wheel"
                  src={colorWheel}
                  width="24px"
                  height="24px"
                />
              </Flex>
              <input
                ref={inputRef}
                className={clsx(styles['hexField'], {
                  [styles['hexField--hasError']]: hasError,
                  [styles['hexField--stretched']]: shouldStretch,
                })}
                type="text"
                onInput={(e) => onChange(e.currentTarget.value)}
                onBlur={onBlur}
                value={value}
                maxLength={7}
                size={9}
              />
            </Flex>
            <input
              id={inputId}
              className={styles['colorField']}
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          </Flex>
        </Card.Group>
      </label>
    </Flex>
  );
};
